import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Loader2, Image as ImageIcon, ArrowRight } from 'lucide-react'
import Lightbox from '../components/Lightbox'

export default function GalleryPage() {
  const [galleries, setGalleries] = useState([])
  const [loading, setLoading] = useState(true)
  const [activeGallery, setActiveGallery] = useState(null) // { images, index }

  useEffect(() => {
    // Set SEO Meta
    document.title = 'Billedgalleri | Se vores lækre madanretninger | Craft Café'
    const metaDesc = document.querySelector('meta[name="description"]')
    if (metaDesc) {
      metaDesc.setAttribute(
        'content',
        'Se billeder af vores lækre tapas, klassiske danske retter, buffeter og bageriprodukter. Få inspiration til din næste fest hos Craft Café.'
      )
    }

    // Fetch galleries from CMS
    fetch('https://api.dev.auxo.dk/public/content/gallery')
      .then((res) => {
        if (!res.ok) throw new Error('Failed to fetch galleries')
        return res.json()
      })
      .then((data) => {
        if (Array.isArray(data)) {
          setGalleries(data)
        }
        setLoading(false)
      })
      .catch((err) => {
        console.error('Error fetching galleries:', err)
        setLoading(false)
      })
  }, [])

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center pt-[100px]">
        <div className="text-center space-y-3">
          <Loader2 className="mx-auto h-8 w-8 animate-spin text-[#e6d5c3]" />
          <p className="text-xs text-stone-400 font-light uppercase tracking-widest">Henter billeder...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="pt-[70px] md:pt-[100px]">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-[#faf9f6] text-[#1c2b21] border-b border-stone-200">
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/assets/35c1b004-16b7-404c-98d3-c630dfe6edb4/d0137351-6fb2-49e8-b9ff-9d5f7ea8fbce.jpg"
            alt="Craft Café madgalleri"
            className="h-full w-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#faf9f6] via-[#faf9f6]/40 to-[#faf9f6]" />
        </div>

        <div className="relative z-10 mx-auto max-w-4xl px-6 py-20 md:py-28 text-center space-y-6">
          <span className="text-[10px] uppercase tracking-[0.3em] text-[#b89a6c] font-light">
            Billedgalleri
          </span>
          <h1 className="font-serif text-3xl md:text-4xl lg:text-5xl font-light tracking-wide leading-relaxed text-[#1c2b21]">
            Vores madgalleri – se vores lækre anretninger
          </h1>
          <p className="text-xs md:text-sm text-stone-600 max-w-2xl mx-auto leading-relaxed font-light uppercase tracking-wider">
            Lad dig friste af vores billeder af tapas, klassisk dansk mad, catering og mad ud af huset.
          </p>
        </div>
      </section>

      {/* Galleries Grid */}
      <section className="py-16 md:py-24 max-w-7xl mx-auto px-6">
        <div className="space-y-20">
          {galleries.length > 0 ? (
            galleries.map((gallery) => (
              <div key={gallery.id} className="space-y-6">
                <div className="border-b border-stone-200 pb-4 flex flex-col sm:flex-row items-start sm:items-end justify-between gap-4">
                  <div className="space-y-1">
                    <h2 className="font-serif text-xl md:text-2xl font-light text-[#1c2b21] tracking-wide">{gallery.title}</h2>
                    {gallery.description && (
                      <p className="text-stone-600 text-xs font-light leading-relaxed">{gallery.description}</p>
                    )}
                  </div>
                  <Link
                    to={`/${gallery.gallery_slug}`}
                    className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-widest text-[#b89a6c] hover:text-[#1c2b21] transition-colors"
                  >
                    Læs mere om {gallery.title.replace(' Galleri', '').toLowerCase()} <ArrowRight size={12} />
                  </Link>
                </div>

                <div className="grid gap-4 grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
                  {gallery.images && gallery.images.map((img, idx) => (
                    <div
                      key={idx}
                      onClick={() => setActiveGallery({ images: gallery.images, index: idx })}
                      className="group relative aspect-square overflow-hidden border border-stone-200 bg-white cursor-pointer rounded-none shadow-none"
                    >
                      <img
                        src={img}
                        alt={`${gallery.title} - Billede ${idx + 1}`}
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-[#faf9f6]/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <span className="border border-[#b89a6c]/30 bg-white/95 p-2 text-[#b89a6c] transform scale-90 group-hover:scale-100 transition-transform rounded-none">
                          <ImageIcon size={14} />
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-16 border border-stone-200 bg-white rounded-none">
              <p className="text-xs text-stone-500 font-light">Ingen gallerier fundet i øjeblikket.</p>
            </div>
          )}
        </div>
      </section>

      {/* Lightbox for Active Gallery */}
      {activeGallery && (
        <Lightbox
          images={activeGallery.images}
          currentIndex={activeGallery.index}
          onClose={() => setActiveGallery(null)}
          onNavigate={(newIndex) => setActiveGallery((prev) => ({ ...prev, index: newIndex }))}
        />
      )}
    </div>
  )
}
